import { DateTime } from 'luxon';
import moment from 'moment-timezone';

export type SiteDateTimeValue = DateTime | string | number | null | undefined;

export const SITE_DATE_TIME_FORMATS = {
  date: 'dd/MM/yyyy',
  dateMonth: 'dd LLL yyyy',
  shortDate: 'dd/MM/yy',
  dayMonth: 'dd/MM',
  dayNameDate: 'EEE dd/MM',
  dayNameDateYear: 'EEE dd/MM/yyyy',
  hourMeridiem: 'hh\na',
  time: 'hh:mm a',
  timeSeconds: 'hh:mm:ss a',
  dateTime: 'dd/MM/yyyy, hh:mm a',
  dateTimeSeconds: 'dd/MM/yyyy, hh:mm:ss a',
  dateTimeMilliseconds: 'dd/MM/yyyy, hh:mm:ss.SSS a',
  compactDateTime: 'dd/MM, hh:mm a',
} as const;

export type SiteDateTimeFormat = keyof typeof SITE_DATE_TIME_FORMATS;

export interface SiteDateTimePresentation {
  formattedValue: string;
  timeZoneLabel: string;
}

const toDateTime = (value: SiteDateTimeValue, timeZone: string): DateTime | undefined => {
  if (DateTime.isDateTime(value)) {
    return value;
  }

  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return DateTime.fromISO(value, { zone: timeZone });
    }

    return DateTime.fromISO(value, { setZone: true });
  }

  if (typeof value === 'number') {
    return DateTime.fromMillis(value);
  }

  return undefined;
};

export const formatTimeZoneLabel = (dateTime: DateTime): string => {
  const timeZone = dateTime.zoneName ?? 'UTC';

  return moment.tz(dateTime.toMillis(), timeZone).zoneAbbr();
};

export const formatSiteDateTime = (
  value: SiteDateTimeValue,
  timeZone: string | undefined,
  format: SiteDateTimeFormat = 'dateTime'
): SiteDateTimePresentation | undefined => {
  if (!timeZone) {
    return undefined;
  }

  const dateTime = toDateTime(value, timeZone);
  const zonedDateTime = dateTime?.setZone(timeZone);

  if (!zonedDateTime?.isValid) {
    return undefined;
  }

  return {
    formattedValue: zonedDateTime.toFormat(SITE_DATE_TIME_FORMATS[format]).toUpperCase(),
    timeZoneLabel: formatTimeZoneLabel(zonedDateTime),
  };
};

export const formatLiveSiteDateTime = (
  value: SiteDateTimeValue,
  timeZone: string | undefined,
  displayTimeZone = true
): string | undefined => {
  if (!timeZone) {
    return undefined;
  }

  const zonedDateTime = toDateTime(value, timeZone)?.setZone(timeZone);

  if (!zonedDateTime?.isValid) {
    return undefined;
  }

  const dateTime = zonedDateTime.toFormat('hh:mm a, dd LLL').replace('am', 'AM').replace('pm', 'PM');

  return displayTimeZone ? `${dateTime} (${formatTimeZoneLabel(zonedDateTime)})` : dateTime;
};
